/** Solana wallet authentication — supports Phantom, Solflare, and other injected wallets */

import { track } from '../analytics.js';

const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');
const WALLET_AUTH_KEY = 'wallet_auth';

export interface SolanaProvider {
  isPhantom?: boolean;
  isSolflare?: boolean;
  connect(): Promise<{ publicKey?: { toString(): string } } | void>;
  disconnect(): Promise<void>;
  signMessage(message: Uint8Array): Promise<{ signature: Uint8Array }>;
  publicKey: { toString(): string } | null;
  isConnected: boolean;
}

/** Detect available Solana wallet */
export function getProvider(): SolanaProvider | null {
  const win = window as any;
  // Phantom
  if (win.phantom?.solana?.isPhantom) return win.phantom.solana;
  // Solflare
  if (win.solflare?.isSolflare) return win.solflare;
  // Generic solana provider
  if (win.solana) return win.solana;
  return null;
}

/** Check if any Solana wallet is available */
export function isWalletAvailable(): boolean {
  return getProvider() !== null;
}

/** Connect wallet + sign message for proof of ownership */
export async function connectWallet(): Promise<{ address: string; name: string; sessionToken: string } | null> {
  track('wallet_connect_attempted');
  const provider = getProvider();
  if (!provider) {
    track('wallet_not_installed');
    window.open('https://phantom.app/', '_blank');
    return null;
  }

  // Heuristic provider name so we can split funnels by Phantom / Solflare /
  // unknown without leaking the actual provider object.
  const providerName = provider.isPhantom ? 'phantom' : provider.isSolflare ? 'solflare' : 'other';

  try {
    const resp = await provider.connect();
    const pubkey = resp?.publicKey ?? provider.publicKey;
    if (!pubkey) throw new Error('Wallet did not return a public key');
    const address = pubkey.toString();

    const timestamp = Date.now();
    const message = new TextEncoder().encode(`Pump My Snake — wallet session at ${timestamp}`);
    const { signature } = await provider.signMessage(message);
    const sigB64 = btoa(String.fromCharCode(...signature));

    const sessionResp = await fetch(`${SERVER_HTTP_URL}/api/players/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress: address, signature: sigB64, timestamp }),
    });
    if (!sessionResp.ok) {
      const data = await sessionResp.json().catch(() => ({}));
      throw new Error(data.error || `wallet session failed (${sessionResp.status})`);
    }
    const sessionData = await sessionResp.json();
    const sessionToken = String(sessionData.sessionToken || '');
    if (!sessionToken) throw new Error('Wallet session token missing');

    // Save to localStorage
    localStorage.setItem(WALLET_AUTH_KEY, JSON.stringify({ address, sessionToken, timestamp: Date.now() }));

    track('wallet_connected', { provider: providerName });
    return { address, name: shortenAddress(address), sessionToken };
  } catch (err: any) {
    const reason = /reject|denied|cancel/i.test(err?.message ?? '')
      ? 'user_cancelled'
      : 'unknown';
    track('wallet_connect_failed', { provider: providerName, reason });
    console.error('Wallet connection failed:', err);
    return null;
  }
}

/** Disconnect wallet */
export async function disconnectWallet(): Promise<void> {
  const provider = getProvider();
  if (provider?.isConnected) {
    try { await provider.disconnect(); } catch { /* ignore */ }
  }
  localStorage.removeItem(WALLET_AUTH_KEY);
}

/** Check if wallet was previously connected (auto-reconnect) */
export function getSavedWallet(): { address: string; name: string; sessionToken: string } | null {
  try {
    const saved = localStorage.getItem(WALLET_AUTH_KEY);
    if (!saved) return null;
    const data = JSON.parse(saved);
    // Expire after 7 days
    if (!data.sessionToken || Date.now() - data.timestamp > 7 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(WALLET_AUTH_KEY);
      return null;
    }
    return { address: data.address, name: shortenAddress(data.address), sessionToken: data.sessionToken };
  } catch {
    return null;
  }
}

export function getWalletSessionToken(walletAddress?: string | null): string | null {
  try {
    const saved = localStorage.getItem(WALLET_AUTH_KEY);
    if (!saved) return null;
    const data = JSON.parse(saved);
    if (!data.sessionToken) return null;
    if (walletAddress && data.address !== walletAddress) return null;
    if (Date.now() - data.timestamp > 7 * 24 * 60 * 60 * 1000) return null;
    return data.sessionToken;
  } catch {
    return null;
  }
}

/** Shorten wallet address for display: "7xKX...3nPq" */
export function shortenAddress(addr: string): string {
  if (addr.length <= 10) return addr;
  return addr.slice(0, 4) + '...' + addr.slice(-4);
}
